import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, Facebook, Instagram, Share2, Info, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tooltip } from "@/components/ui/tooltip";
import { Switch } from "./Switch";
import { itemVariants } from "./motion";
import { usePlatformIntegrations } from "@/features/integrations/hooks/usePlatformIntegrations";
import type { ChannelAutomation, ChannelId } from "../mockAutomations";
import { cn } from "@/lib/utils";

const CHANNEL_ICON: Record<ChannelId, typeof MessageCircle> = {
  messenger: Facebook,
  instagram: Instagram,
  whatsapp: MessageCircle,
};

const ON_COLOR: Record<ChannelId, string> = {
  messenger: "bg-channel-messenger",
  instagram: "bg-channel-instagram",
  whatsapp: "bg-channel-whatsapp",
};

const ICON_TEXT_CLASS: Record<ChannelId, string> = {
  messenger: "text-channel-messenger",
  instagram: "text-channel-instagram",
  whatsapp: "text-channel-whatsapp",
};

const ICON_BG_CLASS: Record<ChannelId, string> = {
  messenger: "bg-channel-messenger/10",
  instagram: "bg-channel-instagram/10",
  whatsapp: "bg-channel-whatsapp/10",
};

interface ChannelsCardProps {
  channels: ChannelAutomation[];
  onToggle: (id: ChannelId, enabled: boolean) => void;
}

export function ChannelsCard({ channels, onToggle }: ChannelsCardProps) {
  const { connectedPlatforms, isLoading } = usePlatformIntegrations();

  const isConnectedMap: Record<ChannelId, boolean> = {
    messenger: (connectedPlatforms?.accounts?.facebook?.length ?? 0) > 0,
    instagram: (connectedPlatforms?.accounts?.instagram?.length ?? 0) > 0,
    whatsapp: (connectedPlatforms?.accounts?.whatsapp?.length ?? 0) > 0,
  };

  const activeCount = channels.filter((c) => {
    const connected = !isLoading && connectedPlatforms ? isConnectedMap[c.id] : c.connected;
    return c.enabled && connected;
  }).length;

  return (
    <motion.div variants={itemVariants}>
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Share2 className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Channel Auto-Reply
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
              {activeCount} Active
            </span>
          </div>
          <Link
            to="/integrations"
            className="text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-0.5"
          >
            Manage Channels
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Content */}
        <div className="p-2.5 bg-white/70 dark:bg-slate-900/70">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {channels.map((channel) => {
              const Icon = CHANNEL_ICON[channel.id];
              const textClass = ICON_TEXT_CLASS[channel.id];
              const bgClass = ICON_BG_CLASS[channel.id];
              const isConnected = !isLoading && connectedPlatforms ? isConnectedMap[channel.id] : channel.connected;

              return (
                <div
                  key={channel.id}
                  className={cn(
                    "flex items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-3 py-2 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-xs",
                    !isConnected && "opacity-85"
                  )}
                >
                  {/* Left: Icon + Name & Status */}
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className={cn("flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-lg shadow-2xs", bgClass, textClass)}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold tracking-tight text-slate-800 dark:text-slate-200 truncate">
                          {channel.label}
                        </span>
                        <Tooltip
                          content={
                            isConnected
                              ? channel.replyWindowNote
                              : `Connect ${channel.label} in Integrations to enable automated replies.`
                          }
                          placement="top"
                        >
                          <Info className="h-3 w-3 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-help shrink-0" />
                        </Tooltip>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] font-medium">
                        {isConnected ? (
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Connected
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400 dark:text-slate-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                            Disconnected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Switch or Connect Action */}
                  <div className="shrink-0 pl-1">
                    {isConnected ? (
                      <Switch
                        checked={channel.enabled}
                        onCheckedChange={(checked) => onToggle(channel.id, checked)}
                        onColorClassName={ON_COLOR[channel.id]}
                        aria-label={`Toggle auto-reply for ${channel.label}`}
                      />
                    ) : (
                      <Link
                        to="/integrations"
                        className="inline-flex items-center gap-0.5 rounded-md border border-primary/20 bg-primary/5 px-2 py-1 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/10 hover:border-primary/30"
                      >
                        Connect
                        <ArrowUpRight className="h-2.5 w-2.5" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
